/**
 * COLONY COMMAND (doc 08) — the meta tree.
 *
 * Three branches, fifteen nodes, every one of them carrying its exact effect.
 * Respec is free, instant and unlimited (the Hades rule): a meta build is an
 * experiment, not a marriage, so the screen never asks "are you sure".
 */

import { L, S } from '../../content/strings.ts';
import { buyNode, respec } from '../../meta/profile.ts';
import { BRANCHES, costOf, NODES, type MetaNode } from '../../meta/tree.ts';
import { font } from '../../render/font/font.ts';
import { PixelSurface } from '../../render/surface.ts';
import { clampScroll, drawBackdrop, drawFrame, drawScrollbar, SCROLLBAR_W, wrapText } from '../../ui/menu.ts';
import { g, UI } from '../../ui/theme.ts';
import { contains, drawPanel, drawStatRow, type Rect } from '../../ui/widgets.ts';
import type { App, Screen } from '../app.ts';
import { TitleScreen } from './title.ts';

const ROW_H = 16;
const HEADER_H = 18;

interface Row {
  node: MetaNode;
  rect: Rect;
}

export class ColonyScreen implements Screen {
  private readonly app: App;
  private readonly nodes: MetaNode[] = BRANCHES.flatMap((b) => b.nodes);
  private rows: Row[] = [];
  private selected = 0;
  private hover = -1;
  private toast: string | null = null;
  private dirty = true;
  private scroll = 0;
  /** Column rect and content height from the last frame — scrolling reads these. */
  private view: Rect | null = null;
  private contentH = 0;
  private lastDraw = 0;
  private readonly started = performance.now();

  constructor(app: App) {
    this.app = app;
  }

  private get node(): MetaNode {
    return this.nodes[this.selected]!;
  }

  private buy(): void {
    const p = this.app.profile;
    if (!p) return;
    const err = buyNode(p, this.node.id);
    this.toast = err ?? `${this.node.name} — INSTALLED.`;
    if (!err) this.app.persist();
    this.dirty = true;
  }

  private respecAll(): void {
    const p = this.app.profile;
    if (!p) return;
    const refund = respec(p);
    this.toast = refund > 0 ? `RESPEC — ${refund} PEARLS BACK. NO CHARGE.` : S.colony.nothingRefund;
    this.app.persist();
    this.dirty = true;
  }

  /** Content-space y of every row, so selection can drag the view with it. */
  private offsets(): number[] {
    const out: number[] = [];
    let y = 0;
    for (const branch of BRANCHES) {
      y += HEADER_H;
      for (const _node of branch.nodes) {
        out.push(y);
        y += ROW_H;
      }
      y += 4;
    }
    this.contentH = y;
    return out;
  }

  private follow(index: number): void {
    if (!this.view) return;
    const offsets = this.offsets();
    const top = (offsets[index] ?? 0) - HEADER_H; // keep the branch label in view
    const bottom = (offsets[index] ?? 0) + ROW_H;
    if (top < this.scroll) this.scroll = top;
    else if (bottom > this.scroll + this.view.h) this.scroll = bottom - this.view.h;
    this.scroll = clampScroll(this.scroll, this.view.h, this.contentH);
  }

  onWheel(e: WheelEvent): void {
    if (!this.view) return;
    e.preventDefault();
    this.offsets();
    this.scroll = clampScroll(this.scroll + Math.sign(e.deltaY) * ROW_H * 2, this.view.h, this.contentH);
    this.dirty = true;
  }

  onKey(e: KeyboardEvent): void {
    const k = e.key.toLowerCase();
    if (k === 'arrowdown' || k === 's') {
      this.selected = (this.selected + 1) % this.nodes.length;
      this.follow(this.selected);
    } else if (k === 'arrowup' || k === 'w') {
      this.selected = (this.selected + this.nodes.length - 1) % this.nodes.length;
      this.follow(this.selected);
    } else if (k === 'enter' || k === ' ') {
      e.preventDefault();
      this.buy();
    } else if (k === 'r') this.respecAll();
    else if (k === 'escape') this.app.go((a) => new TitleScreen(a));
    this.dirty = true;
  }

  onMouseMove(e: MouseEvent): void {
    const { x, y } = this.app.ui(e);
    const at = this.hitRow(x, y);
    if (at !== this.hover) {
      this.hover = at;
      this.dirty = true;
    }
  }

  onMouseDown(e: MouseEvent): void {
    if (e.button === 2) {
      this.app.go((a) => new TitleScreen(a));
      return;
    }
    const { x, y } = this.app.ui(e);
    const at = this.hitRow(x, y);
    if (at >= 0) {
      if (at === this.selected) this.buy();
      this.selected = at;
      this.dirty = true;
    }
  }

  /** Rows scrolled out of the column are not clickable through the furniture. */
  private hitRow(x: number, y: number): number {
    if (this.view && !contains(this.view, x, y)) return -1;
    return this.rows.findIndex((r) => contains(r.rect, x, y));
  }

  resize(): void {
    this.view = null;
    this.dirty = true;
  }

  destroy(): void {}

  update(now: number): void {
    if (!this.dirty && now - this.lastDraw < 50) return;
    const s = this.app.surface;
    const p = this.app.profile;
    drawBackdrop(s, (now - this.started) / 1000);
    const body = drawFrame(s, {
      title: S.colony.colonyCommand,
      subtitle: S.colony.breadthPowerKnowing,
      footer: S.colony.enterBuysR,
      stamp: p ? L.pearls(p.pearls) : undefined,
    });

    const colW = Math.min(330, Math.floor(body.w * 0.52));
    const view: Rect = { x: body.x, y: body.y, w: colW, h: body.h };
    const firstFrame = this.view === null;
    this.view = view;
    this.offsets();
    if (firstFrame) this.follow(this.selected);
    this.scroll = clampScroll(this.scroll, view.h, this.contentH);
    const scrollable = this.contentH > view.h;
    const rowW = scrollable ? colW - SCROLLBAR_W : colW;
    const visible = (top: number, h: number): boolean => top >= view.y && top + h <= view.y + view.h;

    this.rows = [];
    let y = body.y - this.scroll;
    const header = font('header');
    const micro = font('micro');
    const bodyFont = font('body');
    for (const branch of BRANCHES) {
      if (visible(y, header.capHeight)) {
      header.draw(s, branch.name, body.x, y, UI.ink, { caps: true, letterSpacing: 1 });
      const nameW = header.measure(branch.name, { caps: true, letterSpacing: 1 });
      // the blurb is a bonus, not a requirement: it appears only when the
      // column is wide enough to hold it without touching the scrollbar
      const blurbX = body.x + nameW + g(3);
      const blurbW = micro.measure(branch.blurb, { caps: true, letterSpacing: 1 });
      if (blurbX + blurbW <= body.x + rowW) {
        micro.draw(s, branch.blurb, blurbX, y + header.capHeight - micro.capHeight, UI.inkDim, {
          caps: true,
          letterSpacing: 1,
        });
      }
      }
      y += HEADER_H;
      for (const node of branch.nodes) {
        const rect: Rect = { x: body.x, y, w: rowW, h: ROW_H };
        this.rows.push({ node, rect });
        const index = this.rows.length - 1;
        if (!visible(y, ROW_H)) {
          y += ROW_H;
          continue;
        }
        const owned = p?.owned.includes(node.id) ?? false;
        const cost = costOf(node.id, p?.reveals ?? []);
        const affordable = (p?.pearls ?? 0) >= cost;
        const state =
          index === this.selected ? 'selected' : index === this.hover ? 'hover' : 'default';

        if (state !== 'default') {
          s.fillRect(rect.x, rect.y, rect.w, rect.h, state === 'selected' ? UI.panelFillHi : UI.panelFill);
          s.strokeRect(rect.x, rect.y, rect.w, rect.h, state === 'selected' ? UI.accent : UI.panelRimLight);
        }
        const ink = owned ? UI.good : !node.available ? UI.disabled : affordable ? UI.ink : UI.inkDim;
        bodyFont.draw(s, node.name, rect.x + g(1), rect.y + 4, ink, { caps: true });
        const right = owned ? S.colony.owned : !node.available ? S.colony.soon : `${cost}`;
        const rw = bodyFont.measure(right, { caps: true });
        bodyFont.draw(s, right, rect.x + rect.w - g(1) - rw, rect.y + 4, owned ? UI.good : UI.accent, {
          caps: true,
        });
        y += ROW_H;
      }
      y += 4;
    }
    drawScrollbar(s, view, this.scroll, this.contentH);

    this.drawDetail(s, { x: body.x + colW + g(4), y: body.y, w: Math.min(320, body.w - colW - g(4)), h: body.h });
    this.app.hud.upload();
    this.lastDraw = now;
    this.dirty = false;
  }

  private drawDetail(s: PixelSurface, r: Rect): void {
    if (r.w < 150) return;
    const p = this.app.profile;
    const node = this.node;
    const owned = p?.owned.includes(node.id) ?? false;
    const cost = costOf(node.id, p?.reveals ?? []);
    const header = font('header');
    const bodyFont = font('body');
    const effectLines = wrapText('body', node.effect, r.w - g(4));
    const panelH = g(2) + header.capHeight + g(4) + effectLines.length * 11 + g(1) + 14 + 12 + g(2);
    const panel: Rect = { x: r.x, y: r.y, w: r.w, h: Math.min(r.h, panelH) };
    drawPanel(s, panel, { raised: true });
    let y = panel.y + g(2);
    header.draw(s, node.name, panel.x + g(2), y, UI.ink, { caps: true, letterSpacing: 1 });
    y += header.capHeight + g(2);
    s.hline(panel.x + 2, panel.x + panel.w - 3, y, UI.panelRimLight);
    y += g(2);

    for (const line of effectLines) {
      bodyFont.draw(s, line, panel.x + g(2), y, UI.inkDim, { caps: true });
      y += 11;
    }
    y += g(1);
    const status = owned
      ? S.colony.ownedRefundedFull
      : !node.available
        ? S.colony.notWaterYet
        : (p?.pearls ?? 0) >= cost
          ? S.colony.enterBuy
          : `NEEDS ${cost - (p?.pearls ?? 0)} MORE PEARLS`;
    bodyFont.draw(s, status, panel.x + g(2), y, owned ? UI.good : node.available ? UI.accent : UI.warning, {
      caps: true,
    });
    y += 14;
    drawStatRow(s, { x: panel.x + g(2), y, w: panel.w - g(4), h: 8 }, S.colony.price, L.pearls(cost));

    // the running tally, under the card
    let ty = panel.y + panel.h + g(3);
    const ownedCount = p?.owned.length ?? 0;
    drawStatRow(s, { x: r.x, y: ty, w: r.w, h: 8 }, S.colony.nodesOwned, `${ownedCount} / ${Object.keys(NODES).length}`);
    ty += 12;
    drawStatRow(s, { x: r.x, y: ty, w: r.w, h: 8 }, S.colony.pearls, `${p?.pearls ?? 0}`);
    ty += 12;
    if (this.toast) {
      for (const line of wrapText('body', this.toast, r.w)) {
        font('body').draw(s, line, r.x, ty, UI.good, { caps: true });
        ty += 11;
      }
    }
  }
}
